import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar, Car, MapPin, User, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Use the correct backend URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const BookingForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    serviceType: '',
    vehicleType: '',
    pickupLocation: '',
    dropLocation: '',
    date: '',
    time: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.phone || !formData.serviceType || !formData.vehicleType) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/bookings`, formData);
      
      if (response.data.success) {
        toast.success('Booking request received! We will contact you shortly.');
        
        // Reset form
        setFormData({
          name: '',
          phone: '',
          email: '',
          serviceType: '',
          vehicleType: '',
          pickupLocation: '',
          dropLocation: '',
          date: '',
          time: '',
          notes: ''
        });

        // Redirect to home page after 2 seconds
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        toast.error('Failed to submit booking. Please try again.');
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      toast.error('Failed to submit booking. Please call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section id="booking" className="py-24 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] text-xs font-semibold uppercase tracking-[0.2em]">
              Quick Booking
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white mb-4 tracking-tight">
            Book a Cab in Bangalore Now
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Fill out the form below or call us directly at <a href="tel:+917507867648" className="text-[#d4af37] font-semibold hover:underline">+91 75078 67648</a>
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card className="lux-card rounded-2xl shadow-none">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-300 font-semibold flex items-center">
                      <User className="w-4 h-4 mr-2 text-[#d4af37]" />
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 focus:border-[#d4af37] transition-colors"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-300 font-semibold flex items-center">
                      <Phone className="w-4 h-4 mr-2 text-[#d4af37]" />
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+91 XXXXX XXXXX"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 focus:border-[#d4af37] transition-colors"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300 font-semibold flex items-center">
                    <Mail className="w-4 h-4 mr-2 text-[#d4af37]" />
                    Email (Optional)
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 focus:border-[#d4af37] transition-colors"
                  />
                </div>

                {/* Service Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="serviceType" className="text-gray-300 font-semibold">
                      Service Type *
                    </Label>
                    <Select value={formData.serviceType} onValueChange={(value) => handleChange('serviceType', value)}>
                      <SelectTrigger className="bg-[#1a1a1a] border border-white/10 text-white focus:border-[#d4af37]">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                        <SelectItem className="text-gray-200 focus:bg-white/10 focus:text-white" value="airport">Airport Transfer</SelectItem>
                        <SelectItem className="text-gray-200 focus:bg-white/10 focus:text-white" value="outstation">Outstation Trip</SelectItem>
                        <SelectItem className="text-gray-200 focus:bg-white/10 focus:text-white" value="rental">Hourly Rental</SelectItem>
                        <SelectItem className="text-gray-200 focus:bg-white/10 focus:text-white" value="corporate">Corporate Service</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vehicleType" className="text-gray-300 font-semibold flex items-center">
                      <Car className="w-4 h-4 mr-2 text-[#d4af37]" />
                      Vehicle Type *
                    </Label>
                    <Select value={formData.vehicleType} onValueChange={(value) => handleChange('vehicleType', value)}>
                      <SelectTrigger className="bg-[#1a1a1a] border border-white/10 text-white focus:border-[#d4af37]">
                        <SelectValue placeholder="Select vehicle" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                        <SelectItem className="text-gray-200 focus:bg-white/10 focus:text-white" value="sedan">Sedan</SelectItem>
                        <SelectItem className="text-gray-200 focus:bg-white/10 focus:text-white" value="suv">SUV</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Location Details */}
                <div className="space-y-2">
                  <Label htmlFor="pickupLocation" className="text-gray-300 font-semibold flex items-center">
                    <MapPin className="w-4 h-4 mr-2 text-[#d4af37]" />
                    Pickup Location
                  </Label>
                  <Input
                    id="pickupLocation"
                    type="text"
                    placeholder="Enter pickup address"
                    value={formData.pickupLocation}
                    onChange={(e) => handleChange('pickupLocation', e.target.value)}
                    className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 focus:border-[#d4af37] transition-colors"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dropLocation" className="text-gray-300 font-semibold flex items-center">
                    <MapPin className="w-4 h-4 mr-2 text-[#d4af37]" />
                    Drop Location
                  </Label>
                  <Input
                    id="dropLocation"
                    type="text"
                    placeholder="Enter drop address"
                    value={formData.dropLocation}
                    onChange={(e) => handleChange('dropLocation', e.target.value)}
                    className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 focus:border-[#d4af37] transition-colors"
                  />
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="date" className="text-gray-300 font-semibold flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-[#d4af37]" />
                      Pickup Date
                    </Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => handleChange('date', e.target.value)}
                      className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 focus:border-[#d4af37] transition-colors text-base py-6 cursor-pointer"
                      style={{
                        colorScheme: 'dark'
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="time" className="text-gray-300 font-semibold flex items-center">
                      <svg className="w-4 h-4 mr-2 text-[#d4af37]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Pickup Time
                    </Label>
                    <Input
                      id="time"
                      type="time"
                      value={formData.time}
                      onChange={(e) => handleChange('time', e.target.value)}
                      className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 focus:border-[#d4af37] transition-colors text-base py-6 cursor-pointer"
                      style={{
                        colorScheme: 'dark'
                      }}
                    />
                  </div>
                </div>

                {/* Additional Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-gray-300 font-semibold">
                    Additional Notes (Optional)
                  </Label>
                  <Textarea
                    id="notes"
                    placeholder="Any special requests or requirements..."
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 focus:border-[#d4af37] transition-colors min-h-24"
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-full bg-[#d4af37] hover:bg-[#c9a35c] text-black font-semibold text-lg py-6 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
                  </Button>
                </div>

                <p className="text-center text-sm text-gray-400 mt-4">
                  You can also WhatsApp us at <a href="https://wa.me/917507867648" target="_blank" rel="noopener noreferrer" className="text-[#d4af37] font-semibold hover:underline">+91 75078 67648</a>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
